<template>
  <div class="login-page">
    <div class="login-card">
      <div class="brand">
        <img src="@/assets/logo.png" alt="系统Logo" class="logo" />
        <h1 class="title">安徽和丰 WMS</h1>
        <p class="subtitle">移动端登录</p>
      </div>

      <el-form class="login-form" @submit.prevent="handleLogin">
        <el-form-item>
          <el-input
            v-model="form.username"
            placeholder="请输入账号"
            size="large"
            clearable
            autocomplete="username"
            :disabled="loading"
          >
            <template #prefix>
              <span class="field-icon">账</span>
            </template>
          </el-input>
        </el-form-item>

        <el-form-item>
          <el-input
            v-model="form.password"
            :type="showPassword ? 'text' : 'password'"
            placeholder="请输入密码"
            size="large"
            autocomplete="current-password"
            :disabled="loading"
          >
            <template #prefix>
              <span class="field-icon">密</span>
            </template>
            <template #suffix>
              <el-button
                link
                type="primary"
                class="pwd-toggle"
                @click="showPassword = !showPassword"
              >
                {{ showPassword ? "隐藏" : "显示" }}
              </el-button>
            </template>
          </el-input>
        </el-form-item>

        <div class="form-extra">
          <el-checkbox v-model="form.remember" :disabled="loading">
            记住账号和密码
          </el-checkbox>
        </div>

        <el-alert
          v-if="isWeChat"
          class="wechat-tip"
          type="warning"
          :closable="false"
          show-icon
          title="微信内置浏览器可能清理本地缓存，导致记住密码偶尔失效；建议勾选后尽量从同一入口打开，或使用系统浏览器访问。"
        />

        <el-button
          type="primary"
          class="login-btn"
          size="large"
          native-type="submit"
          :loading="loading"
          :disabled="loading"
        >
          {{ loading ? "登录中..." : "登 录" }}
        </el-button>
      </el-form>
    </div>
  </div>
</template>

<script>
import { onMounted, reactive, ref, toRefs } from "vue";
import { useRouter } from "vue-router";
import { ElMessage } from "element-plus";
import request from "@/utils/request";
import md5 from "js-md5";
import { UIC_AUTH_KEYS } from "@/utils/auth.js";

const LOGIN_INFO_KEY = "uic_login_info";
const LOGIN_COOKIE_KEY = "uic_login_info_ck";
const COOKIE_DAYS = 30;

const isWeChatBrowser = () =>
  /MicroMessenger/i.test(navigator.userAgent || "");

const setCookie = (name, value, days) => {
  try {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    // path=/ 尽量覆盖 hash 路由各入口；微信内 cookie 有时比 localStorage 更稳
    document.cookie = `${name}=${encodeURIComponent(
      value,
    )};expires=${expires};path=/;SameSite=Lax`;
  } catch (e) {
    console.warn("写入 cookie 失败:", e);
  }
};

const getCookie = (name) => {
  try {
    const match = document.cookie.match(
      new RegExp(
        "(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)",
      ),
    );
    return match ? decodeURIComponent(match[1]) : "";
  } catch (e) {
    return "";
  }
};

const removeCookie = (name) => {
  try {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Lax`;
  } catch (e) {
    // ignore
  }
};

const saveLoginInfo = (info) => {
  const payload = JSON.stringify({
    username: info.username || "",
    password: info.password || "",
    ts: Date.now(),
  });
  try {
    localStorage.setItem(LOGIN_INFO_KEY, payload);
  } catch (e) {
    console.warn("localStorage 写入失败（常见于微信隐私模式）:", e);
  }
  // 双写 cookie，提升微信内回填成功率
  setCookie(LOGIN_COOKIE_KEY, payload, COOKIE_DAYS);
};

const clearLoginInfo = () => {
  try {
    localStorage.removeItem(LOGIN_INFO_KEY);
  } catch (e) {
    // ignore
  }
  removeCookie(LOGIN_COOKIE_KEY);
};

const loadLoginInfo = () => {
  let raw = "";
  try {
    raw = localStorage.getItem(LOGIN_INFO_KEY) || "";
  } catch (e) {
    raw = "";
  }
  if (!raw) {
    raw = getCookie(LOGIN_COOKIE_KEY) || "";
  }
  if (!raw) return null;
  try {
    const info = JSON.parse(raw);
    if (!info || typeof info !== "object") return null;
    return {
      username: info.username || "",
      password: info.password || "",
    };
  } catch (e) {
    clearLoginInfo();
    return null;
  }
};

export default {
  name: "LoginPage",
  setup() {
    const router = useRouter();
    const isWeChat = ref(isWeChatBrowser());
    const showPassword = ref(false);

    const state = reactive({
      form: {
        username: "",
        password: "",
        remember: false,
      },
      loading: false,
    });

    const { form, loading } = toRefs(state);

    const showLoginError = (message) => {
      ElMessage.error(message || "登录失败，请重试");
    };

    const fillSavedLogin = () => {
      const info = loadLoginInfo();
      if (!info) return;
      form.value.username = info.username;
      form.value.password = info.password;
      form.value.remember = true;
      // 微信/iOS 有时会在首屏后清空 password 输入框，延迟再补一次
      if (info.password) {
        setTimeout(() => {
          if (!form.value.password) {
            form.value.password = info.password;
          }
          form.value.remember = true;
        }, 120);
      }
    };

    onMounted(() => {
      fillSavedLogin();
    });

    const handleLogin = async () => {
      if (loading.value) return;

      if (!form.value.username.trim()) {
        showLoginError("请输入账号");
        return;
      }
      if (!form.value.password.trim()) {
        showLoginError("请输入密码");
        return;
      }

      loading.value = true;

      try {
        const encryptedPassword = md5(form.value.password);
        const response = await request({
          url: "/uic-auth/ma/oauth/token",
          method: "post",
          data: {
            tenantId: "000001",
            username: form.value.username.trim(),
            password: encryptedPassword,
          },
        });

        if (response.data.access_token) {
          if (form.value.remember) {
            saveLoginInfo({
              username: form.value.username.trim(),
              password: form.value.password,
            });
          } else {
            clearLoginInfo();
          }

          // 勾选记住：token 放 localStorage；否则 sessionStorage
          const storage = form.value.remember ? localStorage : sessionStorage;
          storage.setItem(
            UIC_AUTH_KEYS.accessToken,
            response.data.access_token,
          );
          storage.setItem(
            UIC_AUTH_KEYS.refreshToken,
            response.data.refresh_token || "",
          );
          storage.setItem(
            UIC_AUTH_KEYS.tokenType,
            response.data.token_type || "bearer",
          );
          storage.setItem(
            UIC_AUTH_KEYS.userInfo,
            JSON.stringify({
              username: form.value.username.trim(),
              userId: response.data.user_id || "",
              roleName: response.data.role_name || "",
              deptId: response.data.dept_id || "",
              nickName: response.data.nick_name || "",
              account: response.data.account || "",
              roleId: response.data.role_id || "",
            }),
          );

          if (!form.value.remember && isWeChat.value) {
            console.info("微信环境未勾选记住账号：关闭页面后需重新登录");
          }

          router.push("/home").catch((err) => console.warn("路由跳转:", err));
        } else {
          showLoginError("登录失败，未获取到令牌");
        }
      } catch (error) {
        console.error("登录错误:", error);
        showLoginError(error.message || "登录失败，请重试");
      } finally {
        loading.value = false;
      }
    };

    return {
      form,
      loading,
      isWeChat,
      showPassword,
      handleLogin,
    };
  },
};
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 16px;
  padding-bottom: calc(20px + env(safe-area-inset-bottom));
  box-sizing: border-box;
  background:
    radial-gradient(
      120% 80% at 50% -10%,
      rgba(35, 177, 192, 0.28),
      transparent 55%
    ),
    linear-gradient(180deg, #e8f7f9 0%, #f0f2f5 45%, #f5f7fa 100%);
}

.login-card {
  width: 100%;
  max-width: 400px;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 8px 28px rgba(15, 55, 70, 0.1);
  overflow: hidden;
}

.brand {
  text-align: center;
  padding: 28px 20px 20px;
  background: linear-gradient(145deg, #23b1c0 0%, #1a9eac 100%);
  color: #fff;
}

.logo {
  width: 68px;
  height: 68px;
  border-radius: 50%;
  background: #fff;
  padding: 8px;
  box-sizing: border-box;
  margin-bottom: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.subtitle {
  margin: 6px 0 0;
  font-size: 13px;
  opacity: 0.9;
}

.login-form {
  padding: 20px 18px 22px;
}

.login-form :deep(.el-form-item) {
  margin-bottom: 14px;
}

.login-form :deep(.el-input__wrapper) {
  border-radius: 10px;
  min-height: 44px;
}

.field-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  font-size: 12px;
  font-weight: 600;
  color: #23b1c0;
  background: rgba(35, 177, 192, 0.12);
  border-radius: 4px;
}

.pwd-toggle {
  font-size: 13px;
  padding: 0 4px;
}

.form-extra {
  display: flex;
  align-items: center;
  margin: 2px 0 12px;
}

.form-extra :deep(.el-checkbox__label) {
  font-size: 14px;
  color: #606266;
}

.wechat-tip {
  margin-bottom: 14px;
}

.wechat-tip :deep(.el-alert__title) {
  font-size: 12px;
  line-height: 1.45;
  color: #8a6d3b;
}

.login-btn {
  width: 100%;
  height: 44px;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  background: #23b1c0;
  border-color: #23b1c0;
}

.login-btn:hover,
.login-btn:focus {
  background: #1a9eac;
  border-color: #1a9eac;
}

@media (max-width: 360px) {
  .brand {
    padding: 22px 16px 16px;
  }

  .logo {
    width: 60px;
    height: 60px;
  }

  .title {
    font-size: 18px;
  }

  .login-form {
    padding: 16px 14px 18px;
  }
}
</style>
